<template>
  <div class="sidebar" :class="{ 'sidebar-mobile-open': mobileMenuOpen, 'sidebar-collapsed': authStore.sidebarCollapsed }">
    <!-- Logo -->
    <div class="sidebar-header">
      <div class="sidebar-header-content">
        <div class="logo">
          <img src="@/assets/logo.png" alt="QUALI-D" class="logo-icon" />
        </div>
        <div v-if="!authStore.sidebarCollapsed" class="meta-partner-badge">
          <img
            src="https://quali-d.com/resources/Meta_Business_Partners_inline_lockup_negative_primary_RGB.png"
            alt="Meta Business Partner"
            class="meta-partner-logo"
          />
        </div>
      </div>
      <button class="sidebar-toggle-btn" @click="authStore.toggleSidebar()" :title="authStore.sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path v-if="!authStore.sidebarCollapsed" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
          <path v-else d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
        </svg>
      </button>
    </div>

    <!-- Subscription Expired Overlay -->
    <div v-if="authStore.subscriptionExpired && !isAgent" class="subscription-expired-overlay">
      <div class="expired-icon">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
      </div>
      <h3 class="expired-title">Subscription Expired</h3>
      <p class="expired-message">Your subscription has ended. Please renew to continue using QUALI-D.</p>
      <button @click="goToRenew" class="renew-btn">Renew Now</button>
    </div>

    <!-- Navigation -->
    <nav class="sidebar-nav" :class="{ 'nav-disabled': authStore.subscriptionExpired && !isAgent }">
      <div class="nav-section">
        <h3 class="nav-section-title">Main</h3>
        <ul class="nav-list">
          <li class="nav-item">
            <router-link :to="isAgent ? '/agent-dashboard' : '/'" class="nav-link" active-class="active" data-tooltip="Dashboard">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
                </svg>
              </span>
              <span class="nav-text">Dashboard</span>
            </router-link>
          </li>
          <li class="nav-item" v-if="!isAgent">
            <router-link to="/ai-analysis" class="nav-link" data-tooltip="AI Analysis">
              <span class="nav-icon">
                <img src="@/assets/ai-platform-svgrepo-com.svg" width="18" height="18" alt="" />
              </span>
              <span class="nav-text">AI Analysis</span>
              <span class="nav-badge ai">Smart</span>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-section">
        <h3 class="nav-section-title">Communication</h3>
        <ul class="nav-list">
          <li class="nav-item">
            <router-link to="/whatsapp" class="nav-link" data-tooltip="WhatsApp">
              <span class="nav-icon">
                <img src="@/assets/whatsapp-svgrepo-com.svg" width="18" height="18" alt="" />
              </span>
              <span class="nav-text">WhatsApp</span>
              <span v-if="whatsappUnreadCount > 0" class="nav-badge danger">{{ whatsappUnreadCount }}</span>
              <span v-else class="nav-badge">Active</span>
            </router-link>
          </li>
          <!-- WhatsApp Broadcast - Conditional Access Based on Plan -->
          <li class="nav-item" v-if="!isAgent && authStore.canAccessBroadcast()">
            <router-link to="/whatsapp-broadcast" class="nav-link" data-tooltip="WA Broadcast">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,8H4A2,2 0 0,0 2,10V14A2,2 0 0,0 4,16H5V20A1,1 0 0,0 6,21H8A1,1 0 0,0 9,20V16H12L17,20V4L12,8M21.5,12C21.5,13.71 20.54,15.26 19,16.04V7.96C20.54,8.74 21.5,10.29 21.5,12Z"/>
                </svg>
              </span>
              <span class="nav-text">WhatsApp Broadcast</span>
            </router-link>
          </li>
          <!-- WhatsApp Broadcast - Restricted Message for Limited Plans -->
          <li class="nav-item restricted-item" v-if="!isAgent && !authStore.canAccessBroadcast() && authStore.subscription">
            <div class="nav-link restricted-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,8H4A2,2 0 0,0 2,10V14A2,2 0 0,0 4,16H5V20A1,1 0 0,0 6,21H8A1,1 0 0,0 9,20V16H12L17,20V4L12,8M21.5,12C21.5,13.71 20.54,15.26 19,16.04V7.96C20.54,8.74 21.5,10.29 21.5,12Z"/>
                </svg>
              </span>
              <span class="nav-text restricted-text">WhatsApp Broadcast</span>
              <span class="nav-badge locked">Locked</span>
            </div>
            <div class="restricted-message">
              Upgrade To Pro or Starter Plan To Use WhatsApp Marketing Campaigns
            </div>
          </li>
          <li class="nav-item" v-if="!isAgent">
            <router-link to="/whatsapp-templates" class="nav-link" data-tooltip="WA Templates">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14,17H7V15H14M17,13H7V11H17M17,9H7V7H17M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3Z"/>
                </svg>
              </span>
              <span class="nav-text">WhatsApp Templates</span>
            </router-link>
          </li>
          <li class="nav-item flows-spotlight-item" v-if="!isAgent && isAdmin">
            <router-link to="/custom-flows" class="nav-link flows-spotlight-link" :class="{ 'flow-mode-active': authStore.customFlowMode }">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 104.745 104.745" fill="currentColor">
                  <rect x="42.5" y="17.812" width="18.745" height="18.745"/>
                  <rect y="68.188" width="18.745" height="18.745"/>
                  <rect x="42" y="68.188" width="18.745" height="18.745"/>
                  <rect x="86" y="68.188" width="18.745" height="18.745"/>
                  <path d="M98.088,57.084c0.743,0.65,1.062,1.731,0.98,3.306l1.998,0.104c0.117-2.227-0.428-3.834-1.661-4.914c-2.966-2.597-9.188-1.428-18.61,0.338c-7.848,1.473-17.52,3.252-28.497,3.472V40.812h-2V59.39c-10.978-0.22-20.648-1.999-28.496-3.472c-9.419-1.767-15.644-2.935-18.61-0.338c-1.234,1.08-1.778,2.688-1.661,4.914l1.998-0.104c-0.083-1.574,0.237-2.655,0.98-3.306c2.234-1.955,8.716-0.739,16.924,0.801c7.892,1.479,17.604,3.277,28.688,3.511v0.046c0.399,0,0.782-0.02,1.177-0.023c0.396,0.004,0.779,0.023,1.178,0.023v-0.046c11.084-0.233,20.797-2.031,28.688-3.511C89.371,56.345,95.854,55.129,98.088,57.084z"/>
                </svg>
              </span>
              <span class="nav-text">WhatsApp Flows</span>
              <span v-if="authStore.customFlowMode" class="nav-badge flows-badge-live">Live</span>
              <span v-else class="nav-badge flows-badge-star">✦ Flow</span>
            </router-link>
          </li>
          <li class="nav-item" v-if="!isAgent">
            <router-link to="/agents" class="nav-link" data-tooltip="Agents">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M5 9.5C5 7.01472 7.01472 5 9.5 5C11.9853 5 14 7.01472 14 9.5C14 11.9853 11.9853 14 9.5 14C7.01472 14 5 11.9853 5 9.5Z"/>
                  <path d="M14.3675 12.0632C14.322 12.1494 14.3413 12.2569 14.4196 12.3149C15.0012 12.7454 15.7209 13 16.5 13C18.433 13 20 11.433 20 9.5C20 7.567 18.433 6 16.5 6C15.7209 6 15.0012 6.2546 14.4196 6.68513C14.3413 6.74313 14.322 6.85058 14.3675 6.93679C14.7714 7.70219 15 8.5744 15 9.5C15 10.4256 14.7714 11.2978 14.3675 12.0632Z"/>
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M4.64115 15.6993C5.87351 15.1644 7.49045 15 9.49995 15C11.5112 15 13.1293 15.1647 14.3621 15.7008C15.705 16.2847 16.5212 17.2793 16.949 18.6836C17.1495 19.3418 16.6551 20 15.9738 20H3.02801C2.34589 20 1.85045 19.3408 2.05157 18.6814C2.47994 17.2769 3.29738 16.2826 4.64115 15.6993Z"/>
                  <path d="M14.8185 14.0364C14.4045 14.0621 14.3802 14.6183 14.7606 14.7837C15.803 15.237 16.5879 15.9043 17.1508 16.756C17.6127 17.4549 18.33 18 19.1677 18H20.9483C21.6555 18 22.1715 17.2973 21.9227 16.6108C21.9084 16.5713 21.8935 16.5321 21.8781 16.4932C21.5357 15.6286 20.9488 14.9921 20.0798 14.5864C19.2639 14.2055 18.2425 14.0483 17.0392 14.0008L17.0194 14H16.9997C16.2909 14 15.5506 13.9909 14.8185 14.0364Z"/>
                </svg>
              </span>
              <span class="nav-text">Agents</span>
            </router-link>
          </li>
          <li class="nav-item" v-if="!isAgent">
            <router-link to="/departments" class="nav-link" data-tooltip="Departments">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
                </svg>
              </span>
              <span class="nav-text">Departments</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/messages" class="nav-link" data-tooltip="Team Messaging">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z"/>
                </svg>
              </span>
              <span class="nav-text">Team Messaging</span>
            </router-link>
          </li>
          <li class="nav-item" v-if="!isAgent">
            <router-link to="/escalations" class="nav-link" data-tooltip="Escalations">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                </svg>
              </span>
              <span class="nav-text">Escalations</span>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-section">
        <h3 class="nav-section-title">Productivity</h3>
        <ul class="nav-list">
          <li class="nav-item">
            <router-link to="/todo" class="nav-link" data-tooltip="ToDo">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              </span>
              <span class="nav-text">ToDo</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/customers" class="nav-link" data-tooltip="Customers">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M 9.5 0 C 8.1252105 0 7 1.1252105 7 2.5 C 7 3.0633617 7.1940199 3.5807403 7.5117188 4 L 7.5 4 C 6.1252115 4 5 5.12521 5 6.5 L 5 10.5 C 5 11.702941 5.8636264 12.715051 7 12.949219 L 7 18.5 C 7 19.328427 7.6715729 20 8.5 20 L 10.5 20 C 11.328427 20 12 19.328427 12 18.5 L 12 12.949219 L 12.001953 12.949219 C 13.13828 12.715051 14 11.702941 14 10.5 L 14 6.5 C 14 5.12521 12.874789 4 11.5 4 L 11.488281 4 C 11.805981 3.5807403 12 3.0633617 12 2.5 C 12 1.1252105 10.87479 0 9.5 0 z M 4.5 1 C 4.285138 1.0000001 4.0801864 1.0362656 3.8808594 1.0878906 C 3.8543284 1.0950956 3.8289194 1.1032681 3.8027344 1.1113281 C 3.6122454 1.1674598 3.4326019 1.2442769 3.2636719 1.3417969 C 2.5137879 1.7746902 2 2.5766257 2 3.5 C 2 4.0633617 2.1940148 4.5807403 2.5117188 5 L 2.5 5 C 1.125211 5 0 6.12521 0 7.5 L 0 10.5 C -1.4802974e-016 11.702941 0.86190784 12.715051 1.9980469 12.949219 L 2 12.949219 L 2 16.5 C 2 17.328427 2.671573 18 3.5 18 L 5 18 L 6 18 L 6 17 L 5 17 L 5 14.910156 L 5 13.5 C 5 13.223858 4.776142 13 4.5 13 C 4.223858 13 4 13.223858 4 13.5 L 4 17 L 3.5 17 C 3.223858 17 3 16.776142 3 16.5 L 3 8.5 C 3 8.2238576 2.776142 8 2.5 8 C 2.223858 8 2 8.2238576 2 8.5 L 2 11.910156 C 1.416196 11.705514 1 11.157682 1 10.5 L 1 7.5 C 1 6.66565 1.66565 6 2.5 6 L 2.5117188 6 C 3.0640037 6 3.5117188 5.5522848 3.5117188 5 C 3.5111458 4.782732 3.440444 4.5712218 3.28125 4.3574219 C 3.122056 4.1436221 3 3.8417718 3 3.5 C 3 2.6656506 3.66565 2 4.5 2 C 4.788118 2 5.0528871 2.0814481 5.2050781 2.1738281 C 5.3572691 2.2662078 5.4473025 2.2925337 5.5390625 2.2929688 C 5.8152055 2.2929688 6.0390625 2.0691111 6.0390625 1.7929688 C 6.0390215 1.6415092 5.9671356 1.5075681 5.8847656 1.4394531 C 5.8023906 1.3713371 5.7722744 1.3560969 5.7402344 1.3417969 C 5.3733144 1.128618 4.952744 1 4.5 1 z M 9.5 1 C 10.33435 1 11 1.6656505 11 2.5 C 11 2.8417718 10.877896 3.1436223 10.71875 3.3574219 C 10.559605 3.5712214 10.488843 3.7827376 10.488281 4 C 10.488281 4.5522847 10.935996 5 11.488281 5 L 11.5 5 C 12.33435 5 13 5.66565 13 6.5 L 13 10.5 C 13 11.157682 12.583804 11.705514 12 11.910156 L 12 7.5 C 12 7.2238576 11.776142 7 11.5 7 C 11.223858 7 11 7.2238576 11 7.5 L 11 18.5 C 11 18.776142 10.776142 19 10.5 19 L 10 19 L 10 13.5 C 10 13.223858 9.7761424 13 9.5 13 C 9.2238576 13 9 13.223858 9 13.5 L 9 19 L 8.5 19 C 8.2238576 19 8 18.776142 8 18.5 L 8 7.5 C 8 7.2238576 7.7761424 7 7.5 7 C 7.2238576 7 7 7.2238576 7 7.5 L 7 11.910156 C 6.4161964 11.705514 6 11.157682 6 10.5 L 6 6.5 C 6 5.66565 6.6656505 5 7.5 5 L 7.5117188 5 C 8.0640035 5 8.5117188 4.5522847 8.5117188 4 C 8.5111567 3.7827376 8.4403749 3.5712202 8.28125 3.3574219 C 8.1221251 3.1436236 8 2.8417718 8 2.5 C 8 1.6656505 8.6656505 1 9.5 1 z M 14.5 1 C 14.047256 1 13.626686 1.128618 13.259766 1.3417969 C 13.227726 1.3560969 13.197609 1.3713371 13.115234 1.4394531 C 13.032864 1.5075681 12.960978 1.6415091 12.960938 1.7929688 C 12.960938 2.0691111 13.184795 2.2929688 13.460938 2.2929688 C 13.552698 2.2925337 13.642731 2.2662078 13.794922 2.1738281 C 13.947113 2.0814481 14.211882 2 14.5 2 C 15.33435 2 16 2.6656505 16 3.5 C 16 3.8417718 15.877944 4.1436221 15.71875 4.3574219 C 15.559556 4.5712218 15.488854 4.782732 15.488281 5 C 15.488281 5.5522847 15.935996 6 16.488281 6 L 16.5 6 C 17.33435 6 18 6.66565 18 7.5 L 18 10.5 C 18 11.157682 17.583804 11.705514 17 11.910156 L 17 8.5 C 17 8.2238576 16.776142 8 16.5 8 C 16.223858 8 16 8.2238576 16 8.5 L 16 16.5 C 16 16.776142 15.776142 17 15.5 17 L 15 17 L 15 13.5 C 15 13.223858 14.776142 13 14.5 13 C 14.223858 13 14 13.223858 14 13.5 L 14 14.910156 L 14 17 L 13 17 L 13 18 L 14 18 L 15.5 18 C 16.328427 18 17 17.328427 17 16.5 L 17 12.949219 L 17.001953 12.949219 C 18.138092 12.715051 19 11.702941 19 10.5 L 19 7.5 C 19 6.12521 17.874789 5 16.5 5 L 16.488281 5 C 16.805985 4.5807403 17 4.0633617 17 3.5 C 17 2.5766256 16.486212 1.7746902 15.736328 1.3417969 C 15.567398 1.2442766 15.387755 1.1674598 15.197266 1.1113281 C 15.171081 1.1032638 15.145672 1.0950959 15.119141 1.0878906 C 14.919814 1.0362653 14.714862 1 14.5 1 z"/>
                </svg>
              </span>
              <span class="nav-text">Customers</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/leads" class="nav-link" data-tooltip="Leads">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z"/>
                </svg>
              </span>
              <span class="nav-text">Leads</span>
            </router-link>
          </li>
          <li class="nav-item" v-if="!isAgent">
            <router-link to="/products-services" class="nav-link" data-tooltip="Products & Services">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.54,10.67l-.54.27-.54-.27-8-4s0,0,0,0h0l8-4.5a1,1,0,0,1,1,0l8,4.5h0s0,0,0,0l-4,2a.24.24,0,0,0-.09-.06l-4-2a1,1,0,0,0-.9,1.78l2.77,1.39ZM3,7.56a.06.06,0,0,1,0,0v9a1,1,0,0,0,.51.87l8,4.49v-10l-.5-.25Zm18,0-4,2V14a1,1,0,0,1-2,0V10.56l-2.5,1.25v10l8-4.49A1,1,0,0,0,21,16.5v-9A.06.06,0,0,1,21,7.56Z"/>
                </svg>
              </span>
              <span class="nav-text">Products/Services</span>
            </router-link>
          </li>
          <li class="nav-item" v-if="authStore.appointmentMode">
            <router-link to="/appointments" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,3H18V1H16V3H8V1H6V3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M19,19H5V8H19V19M5,6V5H19V6H5Z"/>
                </svg>
              </span>
              <span class="nav-text">Appointments</span>
              <span class="nav-badge active">Active</span>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-section" v-if="authStore.restaurantMode && !isAgent">
        <h3 class="nav-section-title">Restaurant Mode</h3>
        <ul class="nav-list">
          <li class="nav-item">
            <router-link to="/restaurant-settings" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
                </svg>
              </span>
              <span class="nav-text">Restaurant Settings</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/restaurant-categories" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6H12L10,4Z"/>
                </svg>
              </span>
              <span class="nav-text">Menu Categories</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/restaurant-menu-items" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z"/>
                </svg>
              </span>
              <span class="nav-text">Menu Items</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/restaurant-orders" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21,16.5C21,16.88 20.79,17.21 20.47,17.38L12.57,21.82C12.41,21.94 12.21,22 12,22C11.79,22 11.59,21.94 11.43,21.82L3.53,17.38C3.21,17.21 3,16.88 3,16.5V7.5C3,7.12 3.21,6.79 3.53,6.62L11.43,2.18C11.59,2.06 11.79,2 12,2C12.21,2 12.41,2.06 12.57,2.18L20.47,6.62C20.79,6.79 21,7.12 21,7.5V16.5M12,4.15L5,8.09V15.91L12,19.85L19,15.91V8.09L12,4.15Z"/>
                </svg>
              </span>
              <span class="nav-text">Orders</span>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-section" v-if="!isAgent">
        <h3 class="nav-section-title">Management</h3>
        <ul class="nav-list">
          <li class="nav-item" v-if="isAdmin">
            <router-link to="/admin/financial" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6,16.5L3,19.44V11H6M11,14.66L9.43,13.32L8,14.64V7H11M16,13L13,16V3H16M18.81,12.81L17,11H22V16L20.21,14.21L13,21.36L9.53,18.34L5.75,22H3L9.47,15.66L13,18.64"/>
                </svg>
              </span>
              <span class="nav-text">Administration</span>
              <span class="nav-badge danger">Admin</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/payments" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20,8H4V6C4,4.89 4.89,4 6,4H18A2,2 0 0,1 20,6V8M4,10V18A2,2 0 0,0 6,20H18A2,2 0 0,0 20,18V10H4M16,13.5A1.5,1.5 0 0,1 14.5,12A1.5,1.5 0 0,1 16,10.5A1.5,1.5 0 0,1 17.5,12A1.5,1.5 0 0,1 16,13.5Z"/>
                </svg>
              </span>
              <span class="nav-text">Payments & Billing</span>
            </router-link>
          </li>
          <li class="nav-item dropdown-item" :class="{ expanded: settingsExpanded }">
            <div class="nav-link dropdown-trigger" @click="toggleSettings">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.82,11.69,4.82,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
                </svg>
              </span>
              <span class="nav-text">Settings</span>
              <span class="dropdown-arrow" :class="{ rotated: settingsExpanded }">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                </svg>
              </span>
            </div>
            <ul class="dropdown-menu" v-show="settingsExpanded">
              <li class="dropdown-nav-item">
                <router-link to="/company-profile" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Company Profile</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/quick-replies" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.71 17.99A1.02 1.02 0 0 1 11 18H5c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2h14c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2h-6.01c-.26 0-.52.11-.71.29l-2.56 2.7zm8.58-13.29l.71-.71-1.41-1.41-.71.71 1.41 1.41z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Quick Replies</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/company-leads" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-4h3v4H4zM13 12.5h3.5l-1-4.5h-2.5v4.5zM12 13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0-8c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Company Leads</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/whatsapp-settings" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.485 3.488z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">WhatsApp Settings</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/whatsapp-profile" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">WhatsApp Profile</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/zoho-integration" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2l3.09 6.26L22 9l-5.09.74L12 16l-4.91-6.26L2 9l6.91-1.74L12 2zm0 2.5L9.5 8.5 4 9.5l5.5.5L12 13.5l2.5-3.5L20 9.5l-5.5-1L12 4.5zM7 18h10v2H7v-2z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Zoho CRM</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/odoo-integration" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Odoo CRM</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/meta-integration" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Meta CAPI</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/settings/google" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="2" y1="12" x2="22" y2="12"/>
                      <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Google</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <a @click.prevent="openChangePasswordModal" class="dropdown-nav-link" href="#">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Change Password</span>
                </a>
              </li>
            </ul>
          </li>
        </ul>
      </div>

      <div class="nav-section" v-if="!isAgent">
        <h3 class="nav-section-title">Help & Resources</h3>
        <ul class="nav-list">
          <li class="nav-item dropdown-item" :class="{ expanded: tutorialsExpanded }">
            <div class="nav-link dropdown-trigger" @click="toggleTutorials">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M11,7V13H13V7H11M11,15V17H13V15H11Z"/>
                </svg>
              </span>
              <span class="nav-text">Tutorials</span>
              <span class="dropdown-arrow" :class="{ rotated: tutorialsExpanded }">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                </svg>
              </span>
            </div>
            <ul class="dropdown-menu" v-show="tutorialsExpanded">
              <li class="dropdown-nav-item">
                <a href="https://www.youtube.com/watch?v=RT4Rm66WGco" target="_blank" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 16.5l6-4.5-6-4.5v9zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">How To Link Your Whatsapp</span>
                </a>
              </li>
              <li class="dropdown-nav-item">
                <a href="https://www.youtube.com/watch?v=fJo6MfGu__c" target="_blank" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 16.5l6-4.5-6-4.5v9zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">How To Add Products & Services</span>
                </a>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </nav>

    <!-- Plan Section -->
    <div class="plan-section">
      <div class="plan-card">
        <div class="plan-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6,2L2,8L12,22L22,8L18,2H6M6.5,4H10.5L11.5,6H8L6.5,4M13.5,4H17.5L16,6H12.5L13.5,4M7.5,8H10.5L12,12L9,8H7.5M11,8H13L12,10L11,8M14.5,8H16.5L15,8L12,12L14.5,8Z"/>
          </svg>
        </div>
        <div class="plan-info">
          <div class="plan-name">{{ userPlan }}</div>
          <div class="plan-label">Current Plan</div>
        </div>
      </div>
    </div>

    <!-- Logout Section -->
    <div class="logout-section">
      <button @click="handleLogout" class="logout-btn">
        <span class="nav-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
          </svg>
        </span>
        <span class="nav-text">Logout</span>
      </button>
    </div>

    <!-- Change Password Modal -->
    <ChangePasswordModal
      v-if="showChangePasswordModal"
      @close="closeChangePasswordModal"
    />
  </div>
</template>

<script>
import { authStore } from '@/store/auth'
import { DashboardService } from '@/services/dashboard'
import ChangePasswordModal from './ChangePasswordModal.vue'

export default {
  name: 'Sidebar', // eslint-disable-line vue/multi-word-component-names
  components: {
    ChangePasswordModal
  },
  props: {
    isAgent: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      mobileMenuOpen: false,
      settingsExpanded: false,
      tutorialsExpanded: false,
      authStore, // Make authStore available to template
      whatsappUnreadCount: 0,
      unreadCountInterval: null,
      agentName: '',
      agentRole: '',
      showChangePasswordModal: false
    }
  },
  computed: {
    userPlan () {
      if (this.isAgent) {
        return this.agentRole || 'Agent'
      }
      return (authStore.user && authStore.user.plan) || 'No Plan'
    },
    isAdmin () {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      return user.role_id === 1
    }
  },
  watch: {
    // Only update sidebar state when mode actually changes, not on route changes
    'authStore.restaurantMode' () {
      // Mode changed, preserve scroll position
      this.preserveScrollOnNextTick()
    },
    'authStore.appointmentMode' () {
      // Mode changed, preserve scroll position
      this.preserveScrollOnNextTick()
    },
    $route (to) {
      // Auto-collapse on /whatsapp, restore on other routes
      if (to.path === '/whatsapp' && !authStore.sidebarCollapsed) {
        authStore.sidebarCollapsed = true
        document.documentElement.style.setProperty('--sidebar-width', '64px')
      } else if (to.path !== '/whatsapp' && authStore.sidebarCollapsed && localStorage.getItem('sidebar-collapsed') !== 'true') {
        authStore.sidebarCollapsed = false
        document.documentElement.style.setProperty('--sidebar-width', '280px')
      }
    }
  },
  mounted () {
    // Initialize sidebar CSS variable and auto-collapse on /whatsapp
    const onWhatsApp = this.$route?.path === '/whatsapp'
    if (onWhatsApp && !authStore.sidebarCollapsed) {
      authStore.sidebarCollapsed = true
    }
    document.documentElement.style.setProperty(
      '--sidebar-width',
      authStore.sidebarCollapsed ? '64px' : '280px'
    )

    // Load agent data if in agent mode
    console.log('🔵 Sidebar mounted with isAgent prop:', this.isAgent)
    if (this.isAgent) {
      const agentData = JSON.parse(localStorage.getItem('agent_data') || '{}')
      this.agentName = agentData.name || 'Agent'
      this.agentRole = agentData.agent_role_name || 'Agent'
      console.log('🔵 Sidebar: Agent mode ENABLED, hiding non-agent items')
    } else {
      console.log('🔴 Sidebar: Agent mode DISABLED, showing all items')
    }

    // Close mobile menu when clicking outside
    document.addEventListener('click', this.closeMobileMenuOnClickOutside)

    // Listen for custom event to toggle mobile menu
    window.addEventListener('toggleMobileMenu', this.handleMobileMenuToggle)

    // Load initial unread count (only for owners, not agents)
    if (!this.isAgent) {
      this.fetchUnreadCount()

      // Poll for unread count every 30 seconds
      this.unreadCountInterval = setInterval(() => {
        this.fetchUnreadCount()
      }, 30000)
    }

    // Preserve scroll position on route changes
    this.$nextTick(() => {
      const navElement = this.$el.querySelector('.sidebar-nav')
      if (navElement) {
        // Restore scroll position if saved
        const savedScrollPosition = sessionStorage.getItem('sidebar-scroll-position')
        if (savedScrollPosition) {
          navElement.scrollTop = parseInt(savedScrollPosition, 10)
        }

        // Save scroll position on scroll
        navElement.addEventListener('scroll', this.handleNavScroll)
      }
    })
  },
  beforeUnmount () {
    document.removeEventListener('click', this.closeMobileMenuOnClickOutside)
    window.removeEventListener('toggleMobileMenu', this.handleMobileMenuToggle)

    // Clear polling interval
    if (this.unreadCountInterval) {
      clearInterval(this.unreadCountInterval)
    }

    // Clean up scroll listener
    const navElement = this.$el?.querySelector('.sidebar-nav')
    if (navElement) {
      navElement.removeEventListener('scroll', this.handleNavScroll)
    }
  },
  methods: {
    handleNavScroll (event) {
      // Save scroll position to sessionStorage
      sessionStorage.setItem('sidebar-scroll-position', event.target.scrollTop)
    },
    preserveScrollOnNextTick () {
      // When mode changes, preserve scroll position
      const navElement = this.$el?.querySelector('.sidebar-nav')
      if (navElement) {
        const currentScroll = navElement.scrollTop
        this.$nextTick(() => {
          const updatedNavElement = this.$el?.querySelector('.sidebar-nav')
          if (updatedNavElement) {
            updatedNavElement.scrollTop = currentScroll
          }
        })
      }
    },
    async fetchUnreadCount () {
      try {
        const result = await DashboardService.getWhatsAppUnreadCount()
        if (result.success) {
          this.whatsappUnreadCount = result.unreadConversations || 0
        }
      } catch (error) {
        console.error('Error fetching WhatsApp unread count:', error)
      }
    },
    handleMobileMenuToggle () {
      this.mobileMenuOpen = !this.mobileMenuOpen
    },
    closeMobileMenuOnClickOutside (event) {
      if (window.innerWidth <= 768 && this.mobileMenuOpen) {
        const sidebar = this.$el
        const hamburger = document.querySelector('.menu-toggle')
        if (sidebar && !sidebar.contains(event.target) && !hamburger?.contains(event.target)) {
          this.mobileMenuOpen = false
        }
      }
    },
    toggleSettings () {
      if (authStore.sidebarCollapsed) {
        authStore.toggleSidebar()
        this.$nextTick(() => { this.settingsExpanded = true })
      } else {
        this.settingsExpanded = !this.settingsExpanded
      }
    },
    toggleTutorials () {
      if (authStore.sidebarCollapsed) {
        authStore.toggleSidebar()
        this.$nextTick(() => { this.tutorialsExpanded = true })
      } else {
        this.tutorialsExpanded = !this.tutorialsExpanded
      }
    },
    goToRenew () {
      this.$router.push('/renew-subscription')
    },
    openChangePasswordModal () {
      this.showChangePasswordModal = true
    },
    closeChangePasswordModal () {
      this.showChangePasswordModal = false
    },
    handleLogout () {
      if (this.isAgent) {
        console.log('Agent logout clicked from sidebar - clearing agent data...')

        // Clear agent data and auth token
        localStorage.removeItem('agent_token')
        localStorage.removeItem('agent_data')
        localStorage.removeItem('auth_token') // Also clear the owner's token

        console.log('Agent storage cleared, redirecting to agent login...')

        // Navigate to agent login
        this.$router.push('/agent-login')
      } else {
        console.log('Owner logout clicked from sidebar - clearing user data...')

        // Force clear localStorage directly
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user_data')

        // Clear auth store
        authStore.logout()

        console.log('Storage cleared, redirecting to login...')

        // Navigate to login
        this.$router.push('/login')
      }
    }
  }
}
</script>

<style scoped>
.sidebar {
  width: 280px;
  height: 100vh;
  background: var(--bg-sidebar);
  border-right: 1px solid var(--border-primary);
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1000;
  transition: background-color 0.3s ease;
}

.sidebar-header {
  padding: 24px 20px;
  border-bottom: 1px solid var(--border-primary);
}

.meta-partner-badge {
  margin-top: 4px;
}

.meta-partner-logo {
  display: block;
  height: 32px;
  width: auto;
  opacity: 0.85;
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-icon {
  height: 32px;
  width: auto;
}

.logo-text {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
}

.sidebar-nav {
  flex: 1;
  padding: 20px 0;
  overflow-y: auto;
}

/* Custom Scrollbar Styling */
.sidebar-nav::-webkit-scrollbar {
  width: 6px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: var(--bg-secondary);
  border-radius: 3px;
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: var(--text-tertiary);
  border-radius: 3px;
  transition: background 0.2s;
}

.sidebar-nav::-webkit-scrollbar-thumb:hover {
  background: var(--text-secondary);
}

.nav-section {
  margin-bottom: 32px;
}

.nav-section-title {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 20px 12px;
}

.nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-item {
  margin: 0;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  color: var(--text-secondary);
  text-decoration: none;
  transition: all 0.2s;
  position: relative;
}

.nav-link:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.nav-item.active .nav-link,
.nav-link.active,
.nav-link.router-link-active {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border-right: 3px solid #3b82f6;
}

.nav-icon {
  font-size: 18px;
  width: 20px;
  display: flex;
  justify-content: center;
}

.nav-text {
  font-size: 14px;
  font-weight: 500;
}

.nav-badge {
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  background: #10b981;
  color: white;
}

.nav-badge.new {
  background: #f59e0b;
}

.nav-badge.active {
  background: #10b981;
  animation: pulse 2s infinite;
}

.flow-mode-active {
  border-left: 3px solid #10b981 !important;
  padding-left: calc(16px - 3px) !important;
}

/* WhatsApp Flows — spotlight item */
.flows-spotlight-link {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.08), rgba(59, 130, 246, 0.06));
  border-left: 2px solid rgba(139, 92, 246, 0.5);
  padding-left: calc(16px - 2px);
}

.flows-spotlight-link::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 60%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.07),
    transparent
  );
  animation: flows-shimmer 3s ease-in-out infinite;
  pointer-events: none;
}

@keyframes flows-shimmer {
  0%   { left: -100%; }
  50%  { left: 150%; }
  100% { left: 150%; }
}

.flows-spotlight-link:hover {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(59, 130, 246, 0.12)) !important;
}

.nav-badge.flows-badge-star {
  background: linear-gradient(135deg, #8b5cf6, #3b82f6);
  animation: pulse 2s infinite;
  font-size: 10px;
  letter-spacing: 0.3px;
}

.nav-badge.flows-badge-live {
  background: #10b981;
  animation: pulse 2s infinite;
}

.nav-badge.danger {
  background: #ef4444;
  animation: pulse 2s infinite;
}

.nav-badge.ai {
  background: linear-gradient(135deg, #667eea, #764ba2);
  animation: pulse 2s infinite;
}

.nav-badge.locked {
  background: #f59e0b;
  color: white;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* Restricted Item Styles */
.restricted-item {
  position: relative;
}

.restricted-link {
  cursor: not-allowed;
  opacity: 0.6;
  background: var(--bg-secondary);
}

.restricted-link:hover {
  background: var(--bg-secondary);
  color: var(--text-secondary);
}

.restricted-text {
  font-weight: 500;
}

.restricted-message {
  font-size: 11px;
  color: #f59e0b;
  padding: 4px 20px 8px 52px;
  line-height: 1.4;
  font-weight: 500;
  background: rgba(245, 158, 11, 0.1);
  margin-top: -8px;
  border-left: 3px solid #f59e0b;
}

/* Dropdown Styles */
.dropdown-item {
  position: relative;
}

.dropdown-trigger {
  cursor: pointer;
  position: relative;
}

.dropdown-arrow {
  margin-left: auto;
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;
  opacity: 0.7;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
  list-style: none;
  margin: 0;
  padding: 0;
  background: var(--bg-secondary) !important;
  border: 1px solid var(--border-primary);
  border-left: 2px solid rgba(59, 130, 246, 0.3);
  margin-left: 20px;
  margin-top: 4px;
  border-radius: 0 8px 8px 0;
  overflow: hidden;
  animation: slideDown 0.2s ease-out;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    max-height: 200px;
    transform: translateY(0);
  }
}

.dropdown-nav-item {
  margin: 0;
}

.dropdown-nav-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  color: var(--text-secondary) !important;
  background: transparent !important;
  text-decoration: none;
  transition: all 0.2s;
  font-size: 13px;
  position: relative;
}

.dropdown-nav-link:hover {
  background: var(--bg-primary) !important;
  color: var(--text-primary) !important;
  padding-left: 20px;
}

.dropdown-nav-link.router-link-active {
  background: rgba(59, 130, 246, 0.15) !important;
  color: #3b82f6 !important;
  border-right: 2px solid #3b82f6;
}

.dropdown-nav-icon {
  font-size: 14px;
  width: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.dropdown-nav-icon svg {
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.dropdown-nav-link:hover .dropdown-nav-icon svg {
  opacity: 1;
}

.dropdown-nav-text {
  font-size: 13px;
  font-weight: 500;
}

/* Expanded state styles */
.dropdown-item.expanded .dropdown-trigger {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.upgrade-card {
  margin: 20px;
  padding: 20px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 16px;
  text-align: center;
  color: white;
}

.upgrade-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

.upgrade-card h4 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
}

.upgrade-card p {
  margin: 0 0 16px;
  font-size: 13px;
  opacity: 0.9;
}

.upgrade-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.upgrade-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Plan Section */
.plan-section {
  margin: 20px;
  margin-bottom: 12px;
}

.plan-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  color: var(--text-primary);
  padding: 14px 16px;
  border-radius: 8px;
}

.plan-icon {
  font-size: 18px;
  opacity: 0.8;
  display: flex;
  align-items: center;
  justify-content: center;
}

.plan-icon svg {
  color: var(--text-primary);
  opacity: 0.9;
}

.plan-info {
  flex: 1;
}

.plan-name {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 2px;
}

.plan-label {
  font-size: 11px;
  opacity: 0.7;
  font-weight: 400;
}

/* Logout Section */
.logout-section {
  margin: 20px;
  margin-top: 8px;
}

.logout-btn {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
}

.logout-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.3);
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
}

.logout-btn:active {
  transform: translateY(0);
}

.logout-btn .nav-icon {
  font-size: 16px;
}

.logout-btn .nav-text {
  flex: 1;
  text-align: left;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
    transition: transform 0.3s ease-in-out;
    z-index: 2000;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  }

  .sidebar-mobile-open {
    transform: translateX(0);
  }

  .sidebar::before {
    content: '';
    position: fixed;
    top: 0;
    left: 280px;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease-in-out;
  }

  .sidebar-mobile-open::before {
    opacity: 1;
    visibility: visible;
  }

  .nav-section-title {
    font-size: 10px;
  }

  .nav-link {
    padding: 14px 20px;
  }

  .nav-text {
    font-size: 14px;
  }

  .dropdown-menu {
    margin-left: 16px;
  }

  .dropdown-nav-link {
    padding: 8px 12px;
    font-size: 12px;
  }

  .dropdown-nav-text {
    font-size: 12px;
  }

  .plan-section {
    margin: 16px;
    margin-bottom: 8px;
  }

  .plan-card {
    padding: 10px 12px;
  }

  .plan-name {
    font-size: 13px;
  }

  .plan-label {
    font-size: 10px;
  }

  .logout-section {
    margin: 16px;
    margin-top: 8px;
  }
}

/* Subscription Expired Styles */
.subscription-expired-overlay {
  margin: 16px;
  padding: 20px 16px;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
  border-radius: 12px;
  text-align: center;
  color: #991b1b;
}

.expired-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
  color: #dc2626;
}

.expired-title {
  font-size: 15px;
  font-weight: 700;
  margin: 0 0 6px;
  color: #7f1d1d;
}

.expired-message {
  font-size: 12px;
  margin: 0 0 14px;
  color: #991b1b;
  line-height: 1.5;
}

.renew-btn {
  width: 100%;
  padding: 10px 16px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
}

.renew-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
}

.nav-disabled {
  pointer-events: none;
  opacity: 0.35;
  user-select: none;
}

/* ── Sidebar toggle button ───────────────────────────────────────── */
.sidebar-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.sidebar-toggle-btn:hover {
  background: var(--bg-primary);
  color: var(--text-primary);
}

/* Make the header a flex row so toggle sits right of logo */
.sidebar-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.sidebar-header-content {
  display: flex;
  flex-direction: column;
}

/* ── Collapsed sidebar ───────────────────────────────────────────── */
.sidebar-collapsed {
  width: 64px;
  transition: width 0.3s ease;
}

/* Allow tooltip to escape the nav container */
.sidebar-collapsed .sidebar-nav {
  overflow: visible;
}

.sidebar-collapsed .nav-text,
.sidebar-collapsed .nav-badge,
.sidebar-collapsed .nav-section-title,
.sidebar-collapsed .plan-info,
.sidebar-collapsed .logo-icon,
.sidebar-collapsed .dropdown-menu,
.sidebar-collapsed .dropdown-arrow,
.sidebar-collapsed .restricted-message {
  display: none;
}

.sidebar-collapsed .logo {
  justify-content: center;
}

.sidebar-collapsed .sidebar-header {
  justify-content: center;
  padding: 16px 10px;
}

.sidebar-collapsed .sidebar-toggle-btn {
  margin: 0 auto;
}

.sidebar-collapsed .nav-link,
.sidebar-collapsed .nav-link.dropdown-trigger {
  padding: 12px;
  justify-content: center;
}

.sidebar-collapsed .nav-icon {
  width: auto;
}

.sidebar-collapsed .nav-section {
  margin-bottom: 8px;
}

.sidebar-collapsed .plan-section {
  margin: 8px;
}

.sidebar-collapsed .plan-card {
  justify-content: center;
  padding: 10px;
}

.sidebar-collapsed .logout-section {
  margin: 8px;
}

.sidebar-collapsed .logout-btn {
  padding: 12px;
  justify-content: center;
}

/* Tooltip for collapsed items */
.sidebar-collapsed .nav-link {
  position: relative;
}

.sidebar-collapsed .nav-link::after {
  content: attr(data-tooltip);
  position: absolute;
  left: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 2000;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.sidebar-collapsed .nav-link:hover::after {
  opacity: 1;
}

/* Normal sidebar transition */
.sidebar {
  transition: width 0.3s ease, background-color 0.3s ease;
}
</style>
